import { Fragment } from 'react';
import { G } from '@react-pdf/renderer';
import { deviceIcons } from 'globalConstants/Icons/Report';
import { clamp, has, isArray, isNull, values } from 'lodash';

/**
 * @function Devices
 * @description Renders device icons (speakers, microphones, cameras) with directional 
 * arrows in a PDF document. Supports both container and area-specific views.
 * 
 * @param {Object} props
 * @param {boolean} props.inContainer - Whether rendering at container level
 * @param {string} props.areaId - Specific area ID to render
 * @param {number} props.scale - Scale factor for dimensions
 * @param {Object} props.content - Room and device configuration
 * @param {Object} props.content.areas - Areas data by ID
 * @param {string} props.device - Device type to render ('speakers', 'microphones', 'cameras')
 * 
 * @description
 * Renders device icons by:
 * 1. Converting area coordinates to PDF space
 * 2. Scaling icons based on document scale (with min/max bounds)
 * 3. Positioning devices according to their location data
 * 4. Adding directional arrows for devices with horizontal angles
 * 
 * @example
 * <Devices
 *   inContainer={true}
 *   scale={0.5}
 *   device="speakers"
 *   content={{
 *     areas: {
 *       area1: {
 *         location: {left: 10, top: 10},
 *         speakers: [{
 *           model: "CM20",
 *           location: {x: 5, y: 5},
 *           horAngle: 45
 *         }]
 *       }
 *     }
 *   }}
 * />
 * 
 * @note
 * - Uses @react-pdf/renderer for PDF output
 * - Supports both array and object-based device layouts
 * - Icons scaled between 0.15 and 0.90 based on document scale
 * - Handles device-specific icons from globalConstants
 * - Directional arrows only rendered for devices with horAngle property
 */
const Devices = (props) => {
  const { inContainer, areaId, scale, content: { areas }, device } = props;

  const areaArray = (areaId ? [areas?.[areaId]] : values(areas)).map(area => ({
    id: area.id,
    ...area.location,
    devices: isArray(area[device]) ? area[device] : area[device]?.layout,
  }));

  const Icon = deviceIcons[device];
  const Arrow = deviceIcons.deviceArrow;
  const iconSize = 0.005;
  const iconScale = clamp(iconSize * scale, 0.13, 0.90);

  const devices = areaArray.flatMap(area => {
    const x0 = 1 + (inContainer ? area.left * scale : 0);
    const y0 = 1 + (inContainer ? area.top * scale : 0);
    return area.devices.map(device => {
      return ({
        model: device.model,
        x: x0 + device.location.x * scale,
        y: y0 + device.location.y * scale,
        angle: has(device, 'horAngle') ? device.horAngle - 90 : null
      })
    });
  });

  return (
    <Fragment>
      {devices.map((device, index) =>
        <G key={`Icon-${index}`} transform={`translate(${device.x} ${device.y}) scale(${iconScale})`} >
          <Icon model={device.model} />
          {!isNull(device.angle) && <Arrow angle={device.angle} />}
        </G>
      )}
    </Fragment>
  )
};

export default Devices;
